import {Box, Stack, Typography, CircularProgress} from '@mui/material';
import {Elements} from '@stripe/react-stripe-js';
import {stripePromise} from '../config/strip';
import PaymentSummaryBox from '../components/PaymentSummaryBox';
import AppointmentSummary from '../components/AppointmentSummary';
import StripeWidget, {StripeWidgetRef} from '../components/StripeWidget';
import {useRef, useState, useCallback, useMemo} from 'react';
import {ToastType} from '$components/Toast/constant';
import toast from '$utils/toast';
import {
  CONTACT_US,
  DEFAULT_VALUES,
  PAYMENT_ERROR_MESSAGES,
  PAYMENT_PROVIDER,
} from '../constant';
import Footer from '../components/Footer';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {mobileStyles, paymentLinkStyles} from './styles';
import {paymentLinkViewStyles} from './styles';
import {
  usePaymentLinkDetails,
  useProcessPaymentLinkCardPayment,
  useProcessPaymentLinkPaypalPayment,
} from './hooks';
import {RoutePath} from '~/constants/routes';
import {
  PaymentData,
  PaymentResponse,
  PaymentSummaryItem,
  PaymentRequest,
} from './types';
import PaymentSuccessIcon from '~/assets/svgs/payment-success.svg?react';

const SUCCESS_MESSAGES = {
  PAYMENT_SUCCESS: 'Payment completed successfully!',
} as const;

export default function PaymentLinkComponent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment_id');
  const paymentId = searchParams.get('payment_id');
  const [selectedPaymentTab, setSelectedPaymentTab] = useState<number>(0);
  const [paymentFailure, setPaymentFailure] = useState<{
    isFailure: boolean;
    message: string;
  }>({
    isFailure: false,
    message: '',
  });

  const {
    appointmentDetails,
    paymentInfo,
    isLoading: isLoadingDetails,
    error: detailsError,
    paymentDetails,
  } = usePaymentLinkDetails(appointmentId || undefined, paymentId || undefined);

  const [isProcessingPayment, setIsProcessingPayment] =
    useState<boolean>(false);
  const stripeWidgetRef = useRef<StripeWidgetRef>(null);

  const {processCardPayment, isProcessingPayment: isProcessingCard} =
    useProcessPaymentLinkCardPayment();

  const {processPaypalPayment, isProcessingPayment: isProcessingPaypal} =
    useProcessPaymentLinkPaypalPayment();

  const isPaymentCompleted = useMemo(
    () =>
      paymentInfo?.status === 'succeeded' ||
      appointmentDetails?.payment?.status === 'succeeded',
    [paymentInfo?.status, appointmentDetails?.payment?.status]
  );

  const finalAmount = useMemo(
    () => paymentInfo?.amount || appointmentDetails?.payment?.amount || 150,
    [paymentInfo?.amount, appointmentDetails?.payment?.amount]
  );

  const isProcessing = useMemo(
    () => isProcessingPayment || isProcessingCard || isProcessingPaypal,
    [isProcessingPayment, isProcessingCard, isProcessingPaypal]
  );

  const paymentSummaryItems = useMemo(
    (): PaymentSummaryItem[] => [
      {
        label: appointmentDetails?.sub_care_type_name || 'Consultation fee',
        amount: finalAmount,
      },
    ],
    [appointmentDetails?.sub_care_type_name, finalAmount]
  );

  const handlePaymentTabChange = useCallback((tabIndex: number) => {
    setSelectedPaymentTab(tabIndex);
  }, []);

  const handleRetryPayment = useCallback(() => {
    setPaymentFailure({isFailure: false, message: ''});
    setSelectedPaymentTab(0);
  }, []);

  const showSuccessToast = useCallback((title: string, message: string) => {
    toast(ToastType.Success, title, message);
  }, []);

  const validatePaymentData = useCallback(
    (paymentData: PaymentData | undefined): boolean => {
      return (
        !!paymentData &&
        ('id' in paymentData ||
          ('payment' in paymentData && !!paymentData.payment?.id) ||
          ('appointment' in paymentData && !!paymentData.appointment?.id))
      );
    },
    []
  );

  const extractPaymentData = useCallback(
    (response: PaymentResponse): PaymentData | undefined => {
      if (response.data?.appointment)
        return response.data.appointment as PaymentData;
      if (response.data?.payment) return response.data.payment as PaymentData;
      return response.data as PaymentData | undefined;
    },
    []
  );

  const createPaymentRequest = useCallback(
    (
      provider: string,
      additionalData: Record<string, unknown> = {}
    ): PaymentRequest => ({
      payment: {
        provider,
        amount: finalAmount,
        description: 'Payment via payment link',
        ...(appointmentId && {appointment_id: appointmentId}),
        ...(paymentId && !appointmentId && {payment_id: paymentId}),
        ...additionalData,
      },
    }),
    [finalAmount, appointmentId, paymentId]
  );

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const handlePaypalPayment = useCallback(async () => {
    if (isProcessingPayment || isPaymentCompleted) return;

    setIsProcessingPayment(true);
    setPaymentFailure({isFailure: false, message: ''});

    try {
      const paymentRequest = createPaymentRequest(PAYMENT_PROVIDER.PAYPAL);
      const response = await processPaypalPayment(paymentRequest);

      if (!response?.success || !response?.data) {
        setPaymentFailure({
          isFailure: true,
          message: response?.message || PAYMENT_ERROR_MESSAGES.PAYPAL_FAILED,
        });
        return;
      }

      const paymentData = extractPaymentData(response);

      if (!validatePaymentData(paymentData)) {
        setPaymentFailure({
          isFailure: true,
          message: PAYMENT_ERROR_MESSAGES.PAYMENT_PROCESSING_ERROR,
        });
        return;
      }

      if (!response.data.approve_url) {
        setPaymentFailure({
          isFailure: true,
          message: PAYMENT_ERROR_MESSAGES.PAYPAL_NO_URL,
        });
        return;
      }

      window.open(response.data.approve_url, '_blank');
    } catch (error) {
      console.error('PayPal payment error:', error);
      setPaymentFailure({
        isFailure: true,
        message: PAYMENT_ERROR_MESSAGES.UNEXPECTED_ERROR,
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [
    isProcessingPayment,
    isPaymentCompleted,
    createPaymentRequest,
    processPaypalPayment,
    extractPaymentData,
    validatePaymentData,
  ]);

  const handleCardPayment = useCallback(async () => {
    if (isProcessingPayment || isPaymentCompleted) return;

    setIsProcessingPayment(true);
    setPaymentFailure({isFailure: false, message: ''});

    try {
      if (!stripeWidgetRef.current) {
        setPaymentFailure({
          isFailure: true,
          message: PAYMENT_ERROR_MESSAGES.PAYMENT_FORM_NOT_READY,
        });
        return;
      }

      const paymentMethod = await stripeWidgetRef.current.createPaymentMethod();
      if (!paymentMethod) {
        setPaymentFailure({
          isFailure: true,
          message: PAYMENT_ERROR_MESSAGES.INVALID_CARD_DETAILS,
        });
        return;
      }

      const paymentRequest = createPaymentRequest(PAYMENT_PROVIDER.STRIPE, {
        payment_method_id: paymentMethod.id,
      });

      const response = await processCardPayment(paymentRequest);

      if (!response?.success || !response?.data) {
        setPaymentFailure({
          isFailure: true,
          message: response?.message || PAYMENT_ERROR_MESSAGES.PAYMENT_FAILED,
        });
        return;
      }

      const paymentData = extractPaymentData(response);

      if (!validatePaymentData(paymentData)) {
        console.error('Invalid payment data structure:', paymentData);
        setPaymentFailure({
          isFailure: true,
          message: PAYMENT_ERROR_MESSAGES.PAYMENT_PROCESSING_ERROR,
        });
        return;
      }

      showSuccessToast('Payment Success', SUCCESS_MESSAGES.PAYMENT_SUCCESS);

      navigate(RoutePath.paymentSuccessRoute, {
        state: {paymentSuccessData: paymentDetails},
      });
    } catch (error) {
      console.error('Card payment error:', error);
      setPaymentFailure({
        isFailure: true,
        message: PAYMENT_ERROR_MESSAGES.CARD_PAYMENT_ERROR,
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [
    isProcessingPayment,
    isPaymentCompleted,
    createPaymentRequest,
    processCardPayment,
    extractPaymentData,
    validatePaymentData,
    showSuccessToast,
    navigate,
    paymentDetails,
  ]);

  if (isLoadingDetails) {
    return (
      <Box sx={paymentLinkStyles.loadingContainer}>
        <CircularProgress
          size={DEFAULT_VALUES.CIRCLE_PROGRESS_SIZE}
          color="primary"
        />
        <Typography variant="body1" sx={paymentLinkStyles.loadingText}>
          Loading payment details...
        </Typography>
      </Box>
    );
  }

  if (detailsError) {
    return (
      <Box sx={paymentLinkStyles.loadingContainer}>
        <Typography variant="body1" sx={paymentLinkStyles.loadingText}>
          {PAYMENT_ERROR_MESSAGES.LOADING_ERROR}
        </Typography>
      </Box>
    );
  }
  if (paymentFailure.isFailure) {
    return (
      <Box sx={paymentLinkStyles.container}>
        <Box sx={mobileStyles.mobileContainer}>
          <Box sx={mobileStyles.contentWrapper}>
            <Stack gap={1} sx={paymentLinkStyles.stack}>
              <Box sx={paymentLinkStyles.headerBox}>
                <Typography
                  variant="h2"
                  sx={paymentLinkViewStyles.errorTextstyle}
                >
                  There was an issue processing your payment — please try again.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    ...paymentLinkViewStyles.subTextStyle,
                    color: '#FF4D4F',
                  }}
                >
                  {paymentFailure.message}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    ...paymentLinkViewStyles.subTextStyle,
                    marginTop: 2,
                    textAlign: 'center',
                  }}
                >
                  Need help with your payment?
                  <a
                    href={CONTACT_US}
                    style={{
                      color: '#1976d2',
                      textDecoration: 'underline',
                      fontWeight: 500,
                      marginLeft: 4,
                    }}
                  >
                    contact here
                  </a>
                </Typography>
              </Box>

              <PaymentSummaryBox
                totalAmount={finalAmount}
                items={paymentSummaryItems}
                currency="£"
                headerText="Total to pay"
              />

              {appointmentDetails && (
                <AppointmentSummary appointment={appointmentDetails} />
              )}

              <Box sx={paymentLinkStyles.footerBox}>
                <Footer
                  onBackClick={handleBack}
                  onContinueClick={handleRetryPayment}
                  continueButtonLabel="Retry Payment"
                  hideDisclaimer
                />
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  }

  if (paymentFailure.isFailure) {
    return (
      <Box sx={paymentLinkStyles.container}>
        <Box sx={mobileStyles.mobileContainer}>
          <Box sx={mobileStyles.contentWrapper}>
            <Stack gap={1} sx={paymentLinkStyles.stack}>
              <Box sx={paymentLinkStyles.headerBox}>
                <Typography
                  variant="h2"
                  sx={paymentLinkViewStyles.errorTextstyle}
                >
                  There was an issue processing your payment — please try again.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    ...paymentLinkViewStyles.subTextStyle,
                    color: '#FF4D4F',
                  }}
                >
                  {paymentFailure.message}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    ...paymentLinkViewStyles.subTextStyle,
                    marginTop: 2,
                    textAlign: 'center',
                  }}
                >
                  Need help with your payment?
                  <a
                    href={CONTACT_US}
                    style={{
                      color: '#1976d2',
                      textDecoration: 'underline',
                      fontWeight: 500,
                      marginLeft: 4,
                    }}
                  >
                    contact here
                  </a>
                </Typography>
              </Box>

              <PaymentSummaryBox
                totalAmount={finalAmount}
                items={paymentSummaryItems}
                currency="£"
                headerText="Total to pay"
              />

              {appointmentDetails && (
                <AppointmentSummary appointment={appointmentDetails} />
              )}

              <Box sx={paymentLinkStyles.footerBox}>
                <Footer
                  onBackClick={handleBack}
                  onContinueClick={handleRetryPayment}
                  continueButtonLabel="Retry Payment"
                  hideDisclaimer
                />
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  }

  if (isPaymentCompleted) {
    return (
      <Box sx={paymentLinkStyles.container}>
        <Box sx={mobileStyles.mobileContainer}>
          <Box sx={mobileStyles.contentWrapper}>
            <Stack gap={1} sx={paymentLinkStyles.stack}>
              <Box sx={paymentLinkStyles.headerBox}>
                <Box sx={paymentLinkViewStyles.successIconContainer}>
                  <PaymentSuccessIcon className="mb-2" />
                  <Typography
                    variant="h2"
                    sx={paymentLinkViewStyles.successTextstyle}
                  >
                    Payment already processed!
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={paymentLinkViewStyles.successSubTextStyle}
                >
                  This payment link has already been used. Your payment was
                  completed successfully.
                </Typography>
              </Box>
              {appointmentDetails && (
                <AppointmentSummary appointment={appointmentDetails} />
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={paymentLinkStyles.container}>
      <Box sx={mobileStyles.mobileContainer}>
        <Box sx={mobileStyles.contentWrapper}>
          <Stack gap={1} sx={paymentLinkStyles.stack}>
            <Box sx={paymentLinkStyles.headerBox}>
              <Typography variant="h2" sx={paymentLinkViewStyles.textstyle}>
                Complete your payment
              </Typography>
              <Typography
                variant="body2"
                sx={paymentLinkViewStyles.subTextStyle}
              >
                Enter your details to complete the payment
              </Typography>
            </Box>

            <PaymentSummaryBox
              totalAmount={finalAmount}
              items={paymentSummaryItems}
              currency="£"
              headerText="Total to pay"
            />

            <Elements stripe={stripePromise()}>
              <StripeWidget
                ref={stripeWidgetRef}
                className="stripe-widget"
                onPayPalClick={handlePaypalPayment}
                onTabChange={handlePaymentTabChange}
              />
            </Elements>

            {appointmentDetails && (
              <AppointmentSummary appointment={appointmentDetails} />
            )}

            <Box sx={paymentLinkStyles.footerBox}>
              <Footer
                onBackClick={handleBack}
                onContinueClick={handleCardPayment}
                disableContinue={isProcessing || selectedPaymentTab !== 0}
                continueLoading={isProcessing}
                continueButtonLabel="Complete Payment"
                hideDisclaimer
              />
            </Box>
          </Stack>
        </Box>
      </Box>

      {isProcessing && (
        <Box sx={mobileStyles.processLoading}>
          <CircularProgress
            size={DEFAULT_VALUES.CIRCLE_PROGRESS_SIZE}
            color="primary"
          />
          <Typography variant="body1" sx={paymentLinkViewStyles.loadingText}>
            Processing your payment...
          </Typography>
        </Box>
      )}
    </Box>
  );
}
